<script setup lang="ts">
import { ref, useTemplateRef } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { useNotification } from '@kyvg/vue3-notification'
import { pikimoni } from '@/helpers/pikimoni'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import ParentConnectPikimoniModal from '@/ui-kit/parent/ParentConnectPikimoniModal.vue'
import ParentPikimoniInfoModal from '@/ui-kit/parent/ParentPikimoniInfoModal.vue'
import ParentPikimoniSuccessModal from '@/ui-kit/parent/ParentPikimoniSuccessModal.vue'
import PikimoniExamplesModal, { type PikimoniExamplesModalExpose } from '../PikimoniExamplesModal.vue'

interface Student {
  id: number
  name: string
  has_pikimoni_binding: boolean
  needs_pikimoni_binding: boolean
}

interface PageContext {
  students: Student[]
  urls: {
    parent_cabinet: string
    find_teacher: string
    my_trial_lesson_requests: string
  }
}

const ctx = usePageContext<PageContext>()
const { isMobile } = useScreen()
const { notify } = useNotification()

const connectPikimoni = useTemplateRef<{ open: (studentId: number) => void }>('connectPikimoni')
const pikimoniInfo = useTemplateRef<{ open: () => void }>('pikimoniInfo')
const pikimoniExamplesModal = useTemplateRef<PikimoniExamplesModalExpose>('pikimoniExamplesModal')

const students = ref([...ctx.students])

async function loginPikimoni(studentId: number) {
  const win = window.open('about:blank', '_blank')
  if (!win) {
    return
  }

  try {
    const url = await pikimoni.getLoginUrl('student', studentId)
    win.location.href = url
  } catch (err: unknown) {
    console.error(err)
    win.close()

    let title = 'Не удалось перейти ко входу в Pikimoni'
    if (typeof err === 'string') title = err
    if (typeof err === 'object' && err && 'message' in err) title = String(err.message)
    notify({ type: 'error', title })
  }
}
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="parent" rules-url="/website/documents/oferta" />

    <div class="content parent-cabinet-page">
      <v-notifications position="bottom right" />

      <ParentConnectPikimoniModal ref="connectPikimoni" :redirect-url="ctx.urls.parent_cabinet" />
      <ParentPikimoniSuccessModal />
      <ParentPikimoniInfoModal ref="pikimoniInfo" />
      <PikimoniExamplesModal ref="pikimoniExamplesModal" />

      <header class="header">
        <h1 class="header__title">Онлайн-обучение</h1>
      </header>

      <p>Здесь вы можете найти учителя для онлайн-занятий по системе I LOVE ENGLISH.</p>

      <div class="tiles parent-cabinet__tiles mt-4 mt-d-3">
        <div class="tile _form tiles__tile">
          <div class="tile__image mb-4">
            <v-icon width="160" height="190">online-teaching-requests</v-icon>
          </div>
          <h3 class="tile__title text-h3 mb-2">Поиск учителя</h3>
          <p class="tile__description mb-2">Найдите подходящего вам учителя и оставьте заявку на пробный урок</p>
          <v-button mode="primary" class="tile__button" :href="ctx.urls.find_teacher"> Найти учителя </v-button>
        </div>

        <div class="tile _requests tiles__tile">
          <div class="tile__image mb-4">
            <v-icon width="160" height="190">online-teaching-form</v-icon>
          </div>
          <h3 class="tile__title text-h3 mb-2">Заявки</h3>
          <p class="tile__description mb-2">Просматривайте статус ваших заявок</p>
          <v-button mode="primary" class="tile__button" :href="ctx.urls.my_trial_lesson_requests">
            Мои заявки
          </v-button>
        </div>

        <div class="tile _pikimoni tiles__tile">
          <div class="tile__image mb-4">
            <v-icon width="160" height="190">online-teaching-pikimoni</v-icon>
            <v-icon class="tile__icon _pikimoni" width="132" height="40">pikimoni</v-icon>
          </div>

          <h3 class="tile__title text-h3 mb-2">Пикимони</h3>
          <p class="tile__description mb-1">Используйте платформу онлайн-обучения Пикимони.</p>
          <v-link mode="primary" type="button" @click="pikimoniInfo?.open()">Что такое Пикимони?</v-link>
          <v-link class="mt-3 mt-d-2" mode="primary" type="button" @click="pikimoniExamplesModal?.$open()"
            >Примеры уроков</v-link
          >
          <hr class="tile__divider mt-2 mb-3 mb-d-2" />

          <div class="childs-list">
            <div v-for="student in students" :key="student.id" class="childs-list__child child d-flex">
              <span class="child__name text-body-bold pr-1 pb-3 pb-d-2">{{ student.name }}</span>

              <v-button
                v-if="student.has_pikimoni_binding"
                class="child__action"
                :mode="isMobile ? 'primary small' : 'primary xsmall'"
                @click="loginPikimoni(student.id)"
              >
                Войти
              </v-button>

              <v-button
                v-else
                class="child__action"
                :mode="isMobile ? 'primary small' : 'primary xsmall'"
                @click="connectPikimoni?.open(student.id)"
              >
                Подключить
              </v-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/online_teaching/parent_cabinet.scss' as *;
</style>
